// announcements-layer/utils/announcement.constants.ts

/**
 * UI-only sentinel for the "All courses" filter option. Maps to includeAllCourses
 * on the list API; real course UUIDs are sent separately in productIds.
 */
export const ALL_COURSES_FILTER_KEY = 'all-courses'

/** Page size for the infinite announcement list (grid view). */
export const ANNOUNCEMENTS_PAGE_SIZE = 50

/** Default page size for the paginated table view (Figma). */
export const ANNOUNCEMENTS_TABLE_PAGE_SIZE = 10

/** Allowed per-page options for the announcements table. */
export const ANNOUNCEMENTS_TABLE_PAGE_SIZE_OPTIONS = [10, 20, 50] as const

/** Number of page buttons shown in the table pagination footer. */
export const ANNOUNCEMENTS_TABLE_PAGES_TO_DISPLAY = 5

/**
 * Debounce before committing pending viewport "mark as read" ids to the API.
 * Trailing debounce: each newly-seen card resets the timer, so a wider window
 * batches more ids into a single markManyAsRead call (fewer requests while the
 * user scrolls). onScopeDispose flushes any pending ids so none are lost.
 */
export const MARK_READ_DEBOUNCE_MS = 3000

/** List items must stay in the viewport this long before they count as seen (grid + table). */
export const MARK_READ_VISIBILITY_DELAY_MS = 500

/** Debounce before refetching the list after a search keystroke. */
export const SEARCH_DEBOUNCE_MS = 300

/** localStorage key for the learner announcements list view preference. */
export const ANNOUNCEMENTS_VIEW_MODE_STORAGE_KEY = 'cp:announcements-view-mode'

/** Announcements list route. */
export const ANNOUNCEMENTS_ROUTE = '/courses/announcements'

/** Build the announcement detail route for a given id. */
export const announcementDetailRoute = (id: string): string => `/courses/announcements/${id}`

/** Empty-state illustration (shared with memberships admin). */
export const ANNOUNCEMENT_EMPTY_IMAGE = 'https://storage.googleapis.com/revex-membership-staging/assets/announcements_empty_screen.svg'

/** Logo + poster fallback for course announcements when the course has no posterImage. */
export const COURSE_LOGO_PLACEHOLDER = `https://storage.googleapis.com/revex-membership-staging/assets/images/course_logo.png`

/** Analytics source for opening the announcements list from the unread banner or header button. */
export const ANNOUNCEMENT_OPEN_SOURCE = {
  BANNER: 'banner',
  BUTTON: 'button',
} as const

export type AnnouncementOpenSource = (typeof ANNOUNCEMENT_OPEN_SOURCE)[keyof typeof ANNOUNCEMENT_OPEN_SOURCE]

/** Analytics source for opening an announcement detail from the list vs a direct URL. */
export const ANNOUNCEMENT_VIEW_SOURCE = {
  CARD_CLICK: 'card-click',
  DIRECT: 'direct',
} as const

export type AnnouncementViewSource = (typeof ANNOUNCEMENT_VIEW_SOURCE)[keyof typeof ANNOUNCEMENT_VIEW_SOURCE]

/** `history.state` key set when navigating to detail from a list card or table row. */
export const ANNOUNCEMENT_VIEW_NAVIGATION_STATE_KEY = 'announcementViewSource'

/**
 * `history.state` key for offer location set when opening detail from an in-app notification.
 * Must match the key written in notification-layer course-redirect peel.
 * GoKollab only — prefer this over `route.query.locationId` when both exist.
 */
export const ANNOUNCEMENT_LOCATION_NAVIGATION_STATE_KEY = 'announcementLocationId'
